import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { SharedHeader } from "@/components/SharedHeader";
import { Smartphone, Apple, PlayCircle } from "lucide-react";

const CARD_GAME_APPS = [
  {
    name: "Spades Plus",
    category: "Spades",
    description: "The #1 Spades game with millions of players worldwide",
    ios: "https://apps.apple.com/app/spades-plus/id456150375",
    android: "https://play.google.com/store/apps/details?id=com.zynga.spadesplus",
    rating: "4.7"
  },
  {
    name: "Spades Royale",
    category: "Spades",
    description: "Play Spades online with friends and compete in tournaments",
    ios: "https://apps.apple.com/app/spades-royale/id1081180248",
    android: "https://play.google.com/store/apps/details?id=com.beachbumgammon.spadesroyale",
    rating: "4.6"
  },
  {
    name: "VIP Spades",
    category: "Spades",
    description: "Premium Spades experience with multiple game modes",
    ios: "https://apps.apple.com/app/vip-spades/id538523305",
    android: "https://play.google.com/store/apps/details?id=com.casualarena.spades",
    rating: "4.5"
  },
  {
    name: "Hearts+",
    category: "Hearts",
    description: "Classic Hearts card game with online multiplayer",
    ios: "https://apps.apple.com/app/hearts/id535246558",
    android: "https://play.google.com/store/apps/details?id=com.appsorama.heartsplus",
    rating: "4.6"
  },
  {
    name: "Bridge Base Online",
    category: "Bridge",
    description: "World's largest bridge community with free unlimited play",
    ios: "https://apps.apple.com/app/bridge-base-online/id397735498",
    android: "https://play.google.com/store/apps/details?id=com.bridgebase.bbo",
    rating: "4.3"
  },
  {
    name: "Funbridge",
    category: "Bridge",
    description: "Learn and play bridge with AI partners and tournaments",
    ios: "https://apps.apple.com/app/funbridge-bridge-card-game/id458698122",
    android: "https://play.google.com/store/apps/details?id=com.goto.funbridge",
    rating: "4.5"
  },
  {
    name: "Euchre 3D",
    category: "Euchre",
    description: "Beautiful 3D Euchre game with smart AI opponents",
    ios: "https://apps.apple.com/app/euchre-3d/id446032895",
    android: "https://play.google.com/store/apps/details?id=com.nbttech.euchre3d",
    rating: "4.7"
  },
  {
    name: "Pinochle HD",
    category: "Pinochle",
    description: "Single and double-deck Pinochle with intuitive controls",
    ios: "https://apps.apple.com/app/pinochle-hd/id499018148",
    android: "https://play.google.com/store/apps/details?id=com.buzztouch.pinochle",
    rating: "4.4"
  },
  {
    name: "Gin Rummy Plus",
    category: "Rummy",
    description: "The ultimate Gin Rummy experience with live players",
    ios: "https://apps.apple.com/app/gin-rummy-plus/id667930498",
    android: "https://play.google.com/store/apps/details?id=com.zynga.ginrummyplus",
    rating: "4.6"
  },
  {
    name: "Canasta Junction",
    category: "Canasta",
    description: "Modern Canasta game with classic rules and variations",
    ios: "https://apps.apple.com/app/canasta-junction/id1234567890",
    android: "https://play.google.com/store/apps/details?id=com.glowish.canasta",
    rating: "4.5"
  }
];

const BOARD_GAME_APPS = [
  {
    name: "Backgammon Plus",
    category: "Backgammon",
    description: "Premium backgammon with online multiplayer and tournaments",
    ios: "https://apps.apple.com/app/backgammon-plus/id448952413",
    android: "https://play.google.com/store/apps/details?id=com.zynga.backgammonplus",
    rating: "4.6"
  },
  {
    name: "Backgammon Live",
    category: "Backgammon",
    description: "Live backgammon tournaments with players worldwide",
    ios: "https://apps.apple.com/app/backgammon-live/id1025217045",
    android: "https://play.google.com/store/apps/details?id=com.backgammonlive.game",
    rating: "4.5"
  },
  {
    name: "XG Mobile Backgammon",
    category: "Backgammon",
    description: "Professional-level backgammon with world champion AI",
    ios: "https://apps.apple.com/app/xg-mobile-backgammon/id590992580",
    android: "https://play.google.com/store/apps/details?id=com.gammonsite.xgmobile",
    rating: "4.7"
  },
  {
    name: "Checkers Royale",
    category: "Checkers",
    description: "Classic checkers with multiple rule variations",
    ios: "https://apps.apple.com/app/checkers-royale/id1037254640",
    android: "https://play.google.com/store/apps/details?id=com.gemioli.checkersroyale",
    rating: "4.6"
  },
  {
    name: "Checkers Online",
    category: "Checkers",
    description: "Play American and International Draughts online",
    ios: "https://apps.apple.com/app/checkers-online/id595735371",
    android: "https://play.google.com/store/apps/details?id=com.gemioli.checkers",
    rating: "4.5"
  },
  {
    name: "Chess.com",
    category: "Chess",
    description: "The world's largest chess community with lessons and puzzles",
    ios: "https://apps.apple.com/app/chess-play-learn/id329218549",
    android: "https://play.google.com/store/apps/details?id=com.chess",
    rating: "4.8"
  },
  {
    name: "Lichess",
    category: "Chess",
    description: "Free, open-source chess server with no ads",
    ios: "https://apps.apple.com/app/lichess-online-chess/id968371784",
    android: "https://play.google.com/store/apps/details?id=org.lichess.mobileapp",
    rating: "4.7"
  }
];

const OTHER_GAME_APPS = [
  {
    name: "Monopoly GO!",
    category: "Board Games",
    description: "The classic board game reimagined for mobile",
    ios: "https://apps.apple.com/app/monopoly-go/id1621328561",
    android: "https://play.google.com/store/apps/details?id=com.scopely.monopolygo",
    rating: "4.6"
  },
  {
    name: "Catan Universe",
    category: "Board Games",
    description: "Official Settlers of Catan mobile experience",
    ios: "https://apps.apple.com/app/catan-universe/id1082697866",
    android: "https://play.google.com/store/apps/details?id=com.usm.catanuniverse",
    rating: "4.3"
  },
  {
    name: "Ticket to Ride",
    category: "Board Games",
    description: "Build train routes across the country",
    ios: "https://apps.apple.com/app/ticket-to-ride/id432504470",
    android: "https://play.google.com/store/apps/details?id=com.daysofwonder.tt.android",
    rating: "4.5"
  },
  {
    name: "Scrabble GO",
    category: "Word Games",
    description: "The classic word game with new modes and features",
    ios: "https://apps.apple.com/app/scrabble-go/id1215933788",
    android: "https://play.google.com/store/apps/details?id=com.scopely.scrabble",
    rating: "4.4"
  },
  {
    name: "PokerStars",
    category: "Poker",
    description: "World's largest poker site with tournaments and cash games",
    ios: "https://apps.apple.com/app/pokerstars-play-poker-games/id1307277754",
    android: "https://play.google.com/store/apps/details?id=com.pyrsoftware.pokerstars.net",
    rating: "4.5"
  }
];

const AppCard = ({ app }) => (
  <Card className="bg-[#0A0A0A] border border-white/10 hover:border-[#7a6200]/30 transition-colors">
    <CardContent className="p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h4 className="font-bebas text-xl tracking-wider text-white">{app.name}</h4>
          <span className="text-[#7a6200] font-ubuntu text-sm">{app.category}</span>
        </div>
        <span className="bg-[#7a6200]/20 text-[#C6A300] px-2 py-1 rounded text-sm font-ubuntu">
          ★ {app.rating}
        </span>
      </div>
      <p className="text-[#A1A1AA] font-ubuntu text-sm mb-4">{app.description}</p>
      <div className="flex gap-3">
        <a
          href={app.ios}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-black border border-white/20 hover:border-white/40 rounded-lg px-4 py-2 transition-colors"
        >
          <Apple className="w-5 h-5 text-white" />
          <span className="text-white font-ubuntu text-sm">App Store</span>
        </a>
        <a
          href={app.android}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-black border border-white/20 hover:border-white/40 rounded-lg px-4 py-2 transition-colors"
        >
          <PlayCircle className="w-5 h-5 text-white" />
          <span className="text-white font-ubuntu text-sm">Google Play</span>
        </a>
      </div>
    </CardContent>
  </Card>
);

const AppsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="apps-page">
      {/* Shared Header */}
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Hero Section */}
      <section className="pt-16 py-16 px-4 relative">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: "radial-gradient(ellipse at center, rgba(122, 98, 0, 0.3) 0%, transparent 60%)"
          }}
        />
        <div className="max-w-7xl mx-auto relative z-10 text-center pt-8">
          <Smartphone className="w-16 h-16 text-[#7a6200] mx-auto mb-6" />
          <h2 className="font-bebas text-5xl md:text-6xl tracking-wider text-white">
            PLAY ON THE GO
          </h2>
          <p className="mt-6 text-[#A1A1AA] font-ubuntu text-xl max-w-3xl mx-auto">
            Practice your favorite games anytime, anywhere with these top-rated mobile apps 
            available on iOS App Store and Google Play Store.
          </p>
        </div>
      </section>

      {/* Card Game Apps */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-bebas text-3xl tracking-wider text-[#7a6200] mb-8">
            CARD GAME APPS
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CARD_GAME_APPS.map((app, i) => (
              <AppCard key={i} app={app} />
            ))}
          </div>
        </div>
      </section>

      {/* Board Game Apps */}
      <section className="py-12 px-4 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-bebas text-3xl tracking-wider text-[#7a6200] mb-8">
            BACKGAMMON, CHECKERS & CHESS APPS
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BOARD_GAME_APPS.map((app, i) => (
              <AppCard key={i} app={app} />
            ))}
          </div>
        </div>
      </section>

      {/* Other Game Apps */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-bebas text-3xl tracking-wider text-[#7a6200] mb-8">
            OTHER POPULAR GAMES
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {OTHER_GAME_APPS.map((app, i) => (
              <AppCard key={i} app={app} />
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            Board2Boston™ is not affiliated with any of the listed apps or their developers. 
            These recommendations are provided for the convenience of our community members. 
            App availability and features may vary by region.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#050505] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AppsPage;
